/**
 * @author michalstolarski
 * @since 2023-9-29
 */

import * as React from "react";
import styled, { useTheme, css } from "styled-components";

import { rem } from "@ui/helpers";
import { CaretRight } from "@ui/Assets/MyIcons";
import { NormalizedList } from "@ui/Atoms/List";

const List = styled(NormalizedList)`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.margins.base_x3};

  ${props => props.theme.breakpoints.tablet} {
    max-width: ${rem(658)};
  }
`;

const Item = styled.li`
  padding-bottom: ${props => props.theme.margins.half};
  border-bottom: 1px solid ${props => props.theme.palette.blue50};
`;

interface ButtonBodyProps {
  caption?: string;
  children: React.ReactNode;
}

const ButtonBody = (props: ButtonBodyProps): React.ReactElement => {
  const theme = useTheme();

  return (
    <React.Fragment>
      <ButtonContent>
        <span>{props.children}</span>

        {!!props.caption && <ButtonCaption>{props.caption}</ButtonCaption>}
      </ButtonContent>

      <Icon width={theme.icons.sizes.base_x3} />
    </React.Fragment>
  );
};

interface ButtonProps
  extends ButtonBodyProps,
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {}

const Button = ({
  caption,
  children,
  type = "button",
  ...props
}: ButtonProps): React.ReactElement => (
  <ButtonContainer type={type} {...props}>
    <ButtonBody caption={caption}>{children}</ButtonBody>
  </ButtonContainer>
);

interface LinkProps
  extends ButtonBodyProps,
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "children"> {
  disabled?: boolean;
}

const Link = ({
  caption,
  children,
  ...props
}: LinkProps): React.ReactElement => (
  <LinkContainer {...props}>
    <ButtonBody caption={caption}>{children}</ButtonBody>
  </LinkContainer>
);

const buttonLinkStyles = css`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${props => props.theme.margins.base_x3};
  cursor: pointer;

  &:hover {
    color: currentColor;
    text-decoration: none;
  }

  &:disabled,
  &[disabled] {
    color: ${props => props.theme.palette.grey400};
    pointer-events: none;
  }
`;

const ButtonContainer = styled.button`
  all: unset;

  ${buttonLinkStyles}
`;

const LinkContainer = styled.a`
  all: unset;

  ${buttonLinkStyles}
`;

const ButtonContent = styled.div`
  font-size: ${props => props.theme.fonts.sizes.body2};
  line-height: ${props => props.theme.fonts.lineHeights.normal};
  display: flex;
  flex-direction: column;

  ${props => props.theme.breakpoints.tablet} {
    font-size: ${props => props.theme.fonts.sizes.body};
  }
`;

const ButtonCaption = styled.div`
  font-size: ${props => props.theme.fonts.sizes.caption};
  line-height: ${props => props.theme.fonts.lineHeights.caption};

  ${props => props.theme.breakpoints.tablet} {
    font-size: ${props => props.theme.fonts.sizes.subtitle2};
    line-height: ${props => props.theme.fonts.lineHeights.normal};
  }
`;

const Icon = styled(CaretRight)`
  flex: 0 0 auto;
`;

const HelpCenterOptions = {
  List,
  Item,
  Button,
  Link,
};

export { HelpCenterOptions };
